// ./src/components/features/EventVendors/VendorEventsList/VendorEventsList.tsx
//
// ROADMAP #170 — the events one vendor works (Library › Vendors → a vendor → Events): a table on desktop, cards below, like
// every list (owner, 2026-10-20). Each row: the event, its date (on the event's own calendar), what the vendor does there,
// and how far their documents are. A row opens that event's vendor page — the name and the Open button; nothing is
// changed from here, so there is no swipe.
// ⓘ Server-paged (Load more — the page reads it through `useEventVendors` filtered to the vendor).
import React from "react";
import { Paper, Stack, Group, Text, Box, ActionIcon, Tooltip, Badge, Skeleton, Table, UnstyledButton } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { EventVendor } from "@/utils/api";
import { useAppEnvironment } from "@/contexts";
import { formatDateTime } from "@/utils";
import { AppIcon, EmptyState, LoadMoreButton, CardSkeletonActions, CardSkeletonLines, countCardActions } from "@/components/generic";

import { progressLine } from "../vendorUi";

export interface VendorEventsListProps {
  data: EventVendor[];
  /** ⚠ REQUIRED — whether the FIRST read has come back (the skeleton belongs to it alone). ▶ check-list-states */
  isLoaded: boolean;
  /** True when a search is narrowing the list — then an empty one is "no match", not "none yet". */
  hasQueryNarrowing?: boolean;
  onOpen: (row: EventVendor) => void;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

const EventCell: React.FC<{ row: EventVendor }> = ({ row }) => {
  const { t } = useTranslation(["vendors"]);

  return (
    <Stack gap={2} style={{ minWidth: 0 }}>
      <Text size="md" fw={600} truncate>
        {row.eventTitle || t("vendors:events.untitled", { defaultValue: "Untitled event" })}
      </Text>
      {row.serviceDescription ? (
        <Text size="md" c="dimmed" truncate>
          {row.serviceDescription}
        </Text>
      ) : null}
    </Stack>
  );
};

const DateText: React.FC<{ row: EventVendor }> = ({ row }) => (
  <Text size="md" c="dimmed">
    {row.eventStartsAt ? formatDateTime(row.eventStartsAt, "date", row.eventTimezone ?? undefined) : "—"}
  </Text>
);

const DocumentsCell: React.FC<{ row: EventVendor }> = ({ row }) => {
  const { t } = useTranslation(["vendors"]);
  const c = row.counts;

  return (
    <Stack gap={4}>
      <Text size="md" c={c && c.total > 0 && c.approved === c.total ? "green" : undefined}>
        {progressLine(row)}
      </Text>
      {c && c.attention > 0 ? (
        <Group>
          <Badge size="md" variant="light" color="red">
            {t("vendors:list.attention", { defaultValue: "{{n}} need a look", n: c.attention })}
          </Badge>
        </Group>
      ) : null}
    </Stack>
  );
};

const OpenButton: React.FC<{ row: EventVendor; onOpen: (row: EventVendor) => void }> = ({ row, onOpen }) => {
  const { t } = useTranslation(["vendors"]);

  return (
    <Group gap={4} wrap="nowrap" justify="flex-end">
      <Tooltip label={t("vendors:events.open", { defaultValue: "Open the event's vendor page" })} withArrow>
        <ActionIcon
          variant="subtle"
          color="neutral"
          size="lg"
          onClick={() => onOpen(row)}
          aria-label={t("vendors:events.open", { defaultValue: "Open the event's vendor page" })}
        >
          <AppIcon iconName="ChevronRight" size={18} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
};

const VendorEventsListComponent: React.FC<VendorEventsListProps> = ({
  data,
  isLoaded,
  hasQueryNarrowing = false,
  onOpen,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
}) => {
  const { t } = useTranslation(["vendors"]);
  const { isDesktop } = useAppEnvironment();
  // The tablet card's row button (Open) — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onOpen);

  const header = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th>{t("vendors:events.columns.event", { defaultValue: "Event" })}</Table.Th>
        <Table.Th style={{ width: 160 }}>{t("vendors:events.columns.date", { defaultValue: "Date" })}</Table.Th>
        <Table.Th style={{ width: 240 }}>{t("vendors:events.columns.documents", { defaultValue: "Documents" })}</Table.Th>
        <Table.Th style={{ width: 70 }} />
      </Table.Tr>
    </Table.Thead>
  );

  // ⚠ The FIRST read only. ▶ npm run check-list-states
  if (!isLoaded) {
    return (
      <Stack gap="xs" aria-busy="true">
        <Box visibleFrom="md">
          <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              {header}
              <Table.Tbody>
                {[0, 1, 2].map((i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Skeleton height={16} width="55%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width={100} />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width="60%" />
                    </Table.Td>
                    <Table.Td>
                      <Group justify="flex-end">
                        <Skeleton circle height={34} width={34} />
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Box>
        {[0, 1, 2].map((i) => (
          // The phone card: the event, what they do there, the date, the documents.
          <Paper key={`p-${i}`} withBorder radius="md" p="md" hiddenFrom="sm">
            <CardSkeletonLines count={4} gap={6} />
          </Paper>
        ))}
        {[0, 1, 2].map((i) => (
          <Paper key={`t-${i}`} withBorder radius="md" p="md" visibleFrom="sm" hiddenFrom="md">
            <Group gap="md" wrap="nowrap" justify="space-between" align="flex-start">
              <CardSkeletonLines count={4} gap={6} />
              <CardSkeletonActions count={cardActions} />
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  }

  if (data.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("vendors:events.noMatch", { defaultValue: "No events match your search." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("vendors:events.emptyTitle", { defaultValue: "Not on any event yet" })}
          description={t("vendors:events.emptyBody", { defaultValue: "Add them from an event's Vendors page — every event they work shows here." })}
          iconName="CalendarEvent"
          size="md"
        />
      </Paper>
    );
  }

  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            {header}
            <Table.Tbody>
              {data.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <UnstyledButton onClick={() => onOpen(row)} style={{ minWidth: 0, width: "100%" }}>
                      <EventCell row={row} />
                    </UnstyledButton>
                  </Table.Td>
                  <Table.Td>
                    <DateText row={row} />
                  </Table.Td>
                  <Table.Td>
                    <DocumentsCell row={row} />
                  </Table.Td>
                  <Table.Td>
                    <OpenButton row={row} onOpen={onOpen} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
        {loadMore}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {data.map((row) => {
          const cardBody = (showActions: boolean) => (
            <Paper p="md" withBorder radius="md">
              <Group gap="md" wrap="nowrap" align="flex-start">
                <UnstyledButton onClick={() => onOpen(row)} style={{ flex: 1, minWidth: 0 }}>
                  <Stack gap={6}>
                    <EventCell row={row} />
                    <DateText row={row} />
                    <DocumentsCell row={row} />
                  </Stack>
                </UnstyledButton>
                {showActions ? <OpenButton row={row} onOpen={onOpen} /> : null}
              </Group>
            </Paper>
          );

          return (
            <React.Fragment key={row.id}>
              <Box hiddenFrom="sm">{cardBody(false)}</Box>
              <Box visibleFrom="sm">{cardBody(true)}</Box>
            </React.Fragment>
          );
        })}
      </Stack>
      {loadMore}
    </Stack>
  );
};

export const VendorEventsList = React.memo(VendorEventsListComponent);
